import React, { useState, useEffect, useContext } from 'react';

/** context */
import { UserContext } from '../../context/UserContext';
import { validateInputField } from '../../helpers/validation';

/** service */
import { updateApplication, validateProperty } from '../../service/api';

const fields = [
  {
    label: 'Date of Birth',
    limits: { minimum: 1, maximum: 100 },
    name: 'birthDate',
    order: 0,
    pattern: '^(19[0-9][0-9]|20[0-1][0-9]|202[0-1])-(1[0-2]|0[1-9])',
    type: 'date',
    required: true,
  },
  {
    label: 'Type of Residence',
    name: 'housingStatus',
    limits: { minimum: 1, maximum: 100 },
    order: 1,
    type: 'selectOne',
    options: [
      { value: 'OWN', label: 'Own', displayForValue: null },
      { value: 'RENT', label: 'Rent', displayForValue: null },
      { value: 'OTHER', label: 'Other', displayForValue: null },
    ],
    required: true,
  },
  {
    label: 'Monthly Housing Payment',
    name: 'monthlyHousingCost',
    limits: { minimum: 0, maximum: 10000000 },
    order: 2,
    pattern: '^\\d{3}\\-?\\d{2}\\-?\\d{4}$',
    type: 'dollarInput',
    condition: ['RENT', 'OTHER'],
    dependsOn: 'housingStatus',
  },
  {
    label: 'Gross Annual Income',
    name: 'annualRevenue',
    limits: { minimum: 0, maximum: 10000000 },
    order: 3,
    pattern: '^\\d{3}\\-?\\d{2}\\-?\\d{4}$',
    type: 'dollarInput',
    required: true,
  },
  {
    label: 'SSN',
    name: 'ssn',
    limits: { minimum: 9, maximum: 11 },
    order: 4,
    pattern: '^\\d{3}\\-?\\d{2}\\-?\\d{4}$',
    mask: '999-99-9999',
    placeholder: 'xxx-xx-xxxx',
    type: 'maskInput',
    required: true,
  },
];

export default function BnplPersonalDetails({
  className,
  gotoNextStep,
  renderField,
  sanitizeData,
  data,
  setDataError,
  sectionError,
  setSectionError,
}) {
  const { appData, updateAppData, setLoading } = useContext(UserContext);

  const handleSubmit = async () => {
    setDataError({});
    const result = fields.map((field) => {
      if (field.name == 'monthlyHousingCost') {
        field.required = ['RENT', 'OTHER'].includes(data['housingStatus']);
      }
      return validateInputField(data[field.name], field.name, field, setDataError);
    });

    const isInValid = result.filter((r) => !r).length > 0;

    isInValid
      ? setSectionError('Please make sure all fields are filled in correctly.')
      : setSectionError('');

    if (isInValid) {
      return false;
    }

    data['ownershipPercentage'] = 100;
    const sanitizedData = sanitizeData(data);
    setLoading(true);

    try {
      const applicantFeature = appData?.applicationFeatures.find(
        (feature) => feature.dataType === 'applicant'
      );

      const updatedData = await updateApplication([
        {
          itemType: applicantFeature.itemType,
          dataType: applicantFeature.dataType,
          name: applicantFeature.name,
          value: [{ ...sanitizedData }],
        },
      ]);

      if (updatedData.data) {
        updateAppData(updatedData.data);
      }

      const validationData = await validateProperty(applicantFeature.name);

      setLoading(false);

      if (validationData.data && validationData.data.errors) {
        if (validationData.data.errors.length) {
          validationData.data.errors.map((error) => {
            const attributes = error.property.split('.');
            const fieldName = attributes[2];
            setDataError((dataError) => ({
              ...dataError,
              [fieldName]: dataError[fieldName]
                ? dataError[fieldName].concat('. ', error.message)
                : error.message,
            }));
          });
          return false;
        }
      }
    } catch (error) {
      const errors = error.response.data.errors;
      if (errors.length) {
        setError(errors[0].message);
      }
      setLoading(false);
      return;
    }

    gotoNextStep();
  };

  return (
    <>
      {sectionError && <p className="section-error">{sectionError}</p>}
      <div className={className}>
        <div className={`${className}__title`}>
          <h1>Additional personal details</h1>
          <p>
            Please complete information below to continue your Lendistry Interactive application.
          </p>
        </div>
        {fields
          .sort((field1, field2) => field1.order - field2.order)
          .map((field) => renderField(field))}
        <div className={`${className}__button-container margin-top-32`}>
          <button type="submit" onClick={handleSubmit}>
            Next
          </button>
        </div>
      </div>
    </>
  );
}
